import React from "react";
import { Building2, User, Calendar as CalendarIcon, Plus, Trash2, RefreshCcw } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { settingsLabels } from "../labels";

interface ExceptionsTabProps {
  loading: boolean;
  exceptions: any[];
  professionals: any[];
  weekdayNames: string[];
  lang: string;
  handleDelete: (id: number) => void;
  onAddClick: () => void;
}

export function ExceptionsTab({
  loading,
  exceptions,
  professionals,
  weekdayNames,
  lang,
  handleDelete,
  onAddClick,
}: ExceptionsTabProps) {
  const t = settingsLabels[lang].exceptions;

  return (
    <div className="flex flex-col gap-6 animate-in slide-in-from-bottom-4 duration-700 w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="space-y-1">
          <h2 className="text-sm font-bold text-neutral-800 uppercase tracking-widest">{t.tabs.exceptions}</h2>
          <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest">
            {exceptions.length} {lang === 'pt' ? 'registros encontrados' : 'records found'}
          </p>
        </div>
        <Button 
          onClick={onAddClick}
          className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-widest rounded-2xl transition-all active:scale-95"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t.new}
        </Button>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-4 text-center">
            <div className="h-8 w-8 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin" />
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">{t.loading}</p>
          </div>
        ) : exceptions.length === 0 ? (
          <div className="rounded-[2.5rem] border-2 border-dashed border-neutral-100 p-20 flex flex-col items-center justify-center gap-4 text-center grayscale opacity-60">
             <div className="w-20 h-20 rounded-[2.5rem] bg-neutral-50 flex items-center justify-center">
                <CalendarIcon className="h-10 w-10 text-neutral-300" />
             </div>
             <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">{t.none}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {exceptions.map((exc) => (
              <div
                key={exc.id}
                className="group flex items-center justify-between rounded-[2rem] border border-neutral-100 bg-white p-6 shadow-sm hover:border-neutral-200 transition-all duration-300 animate-in fade-in"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    {exc.employee_id ? (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 text-blue-700 text-[9px] font-bold uppercase tracking-widest">
                        <User className="h-3 w-3" />
                        <span>{professionals.find((p) => p.id === exc.employee_id)?.name || "Profissional"}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase tracking-widest">
                        <Building2 className="h-3 w-3" />
                        <span>{t.fields.allProfessionals}</span>
                      </div>
                    )}
                    {(exc.repeat_weekday !== null || exc.repeat_month_day !== null) && (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-purple-50 text-purple-700 text-[9px] font-bold uppercase tracking-widest">
                        <RefreshCcw className="h-3 w-3" />
                        <span>{exc.repeat_weekday !== null ? (lang === "pt" ? "Semanal" : "Weekly") : (lang === "pt" ? "Mensal" : "Monthly")}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 text-sm font-bold text-neutral-800 tracking-tight">
                      <div className="w-8 h-8 rounded-xl bg-neutral-50 flex items-center justify-center text-neutral-400 shrink-0">
                        {exc.repeat_weekday !== null || exc.repeat_month_day !== null ? (
                          <RefreshCcw className="h-4 w-4" />
                        ) : (
                          <CalendarIcon className="h-4 w-4" />
                        )}
                      </div>
                      {(() => {
                        const s = new Date(exc.start_datetime.replace(" ", "T"));
                        const e = new Date(exc.end_datetime.replace(" ", "T"));
                        const isAllDay = s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 23 && e.getMinutes() === 59;

                        if (exc.repeat_weekday !== null) {
                          const dayName = weekdayNames[exc.repeat_weekday];
                          if (isAllDay) return `${lang === "pt" ? "Toda" : "Every"} ${dayName} (${t.fields.allDay})`;
                          return `${lang === "pt" ? "Toda" : "Every"} ${dayName}: ${s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
                        }

                        if (exc.repeat_month_day !== null) {
                          if (isAllDay) return `${lang === "pt" ? "Todo dia" : "Every"} ${exc.repeat_month_day} (${t.fields.allDay})`;
                          return `${lang === "pt" ? "Todo dia" : "Every"} ${exc.repeat_month_day}: ${s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
                        }

                        if (isAllDay) return s.toLocaleDateString() + " (" + t.fields.allDay + ")";

                        return (
                          s.toLocaleString() + " - " + (s.toLocaleDateString() === e.toLocaleDateString() ? e.toLocaleTimeString() : e.toLocaleString())
                        );
                      })()}
                    </div>
                    {exc.description && (
                      <div className="ml-11 px-4 py-2 rounded-2xl bg-neutral-50 border-l-4 border-emerald-500 text-xs font-bold text-neutral-600 italic">
                        {exc.description}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-12 h-12 rounded-2xl text-neutral-300 hover:text-red-600 hover:bg-red-50 transition-all active:scale-90"
                  onClick={() => handleDelete(exc.id)}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
